import { Link } from "@inertiajs/react"
import { Eye, Play, Plus, X } from "lucide-react"
import { useState } from "react"

import VideoPlayer from "@/components/players/video-player"
import ShareButton from "@/components/share-button"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { Highlight, HighlightWithStreamTrait } from "@/types/serializers"

type HighlightWithStream = Highlight & HighlightWithStreamTrait

interface Props {
  highlights: HighlightWithStream[]
  playerName: string
  playerSlug: string
  isOwnProfile?: boolean
}

function getYouTubeThumbnailUrl(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^&?/\s]+)/,
  )
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "0:00"
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${String(secs).padStart(2, "0")}`
}

export default function HighlightsGrid({
  highlights,
  playerName,
  playerSlug,
  isOwnProfile,
}: Props) {
  const [activeHighlight, setActiveHighlight] =
    useState<HighlightWithStream | null>(null)

  if (highlights.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">Aún no se agregaron highlights</p>
        {isOwnProfile && (
          <Button variant="outline" size="sm" className="mt-3" asChild>
            <Link href="/perfil/highlights">
              <Plus className="mr-1.5 h-4 w-4" />
              Agregar highlight
            </Link>
          </Button>
        )}
      </div>
    )
  }

  const sorted = [...highlights].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1
    if (!a.pinned && b.pinned) return 1
    return 0
  })

  const displayed = sorted.slice(0, 6)

  return (
    <div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {displayed.map((highlight) => (
          <div
            key={highlight.id}
            className="group bg-card overflow-hidden rounded-lg border shadow-sm"
          >
            {activeHighlight?.id === highlight.id ? (
              <div className="relative">
                <button
                  className="absolute top-2 right-2 z-10 rounded-full bg-black/60 p-1.5 text-white"
                  onClick={() => setActiveHighlight(null)}
                  aria-label="Cerrar video"
                >
                  <X className="h-5 w-5" />
                </button>
                <VideoPlayer
                  streamEmbedUrl={highlight.stream_embed_url}
                  videoUrl={highlight.video_url}
                  streamStatus={highlight.stream_status}
                />
              </div>
            ) : (
              <button
                className="relative w-full cursor-pointer"
                onClick={() => setActiveHighlight(highlight)}
                aria-label={`Reproducir: ${highlight.title}`}
              >
                <div className="bg-secondary relative aspect-video">
                  {highlight.thumbnail_url ? (
                    <img
                      src={highlight.thumbnail_url}
                      alt={highlight.title}
                      className="h-full w-full object-cover"
                    />
                  ) : highlight.video_url &&
                    getYouTubeThumbnailUrl(highlight.video_url) ? (
                    <img
                      src={getYouTubeThumbnailUrl(highlight.video_url)!}
                      alt={highlight.title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <video
                      src={highlight.video_url ?? undefined}
                      preload="metadata"
                      className="h-full w-full object-cover"
                      muted
                      playsInline
                    />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/40">
                    <Play className="h-12 w-12 text-white drop-shadow-lg" />
                  </div>
                  <Badge className="absolute right-2 bottom-2 bg-black/70 text-white">
                    {formatDuration(highlight.duration_seconds)}
                  </Badge>
                </div>
              </button>
            )}
            <div className="p-3">
              <div className="flex items-start justify-between gap-2">
                <p
                  data-testid="highlight-title"
                  className="leading-snug font-medium"
                >
                  {highlight.title}
                </p>
                <ShareButton
                  url={`${window.location.origin}/jugadores/${playerSlug}`}
                  title={`¡Mirá este highlight de ${playerName}! ${window.location.origin}/jugadores/${playerSlug}`}
                  variant="icon"
                />
              </div>
              <p className="text-muted-foreground mt-1 flex items-center gap-1 text-xs">
                <Eye className="h-3 w-3" />
                {highlight.views_count} vistas
              </p>
            </div>
          </div>
        ))}
      </div>

      {highlights.length > 6 && (
        <div className="mt-4 text-center">
          <span className="text-krown-300 hover:text-krown-200 cursor-pointer text-sm font-medium">
            Ver todos
          </span>
        </div>
      )}
    </div>
  )
}
